/**
 * Marriage & Family Q&A cluster (registration, affirmation, Non-O marriage,
 * prenup, divorce, custody, adoption, dependent visa, naturalization).
 * Deterministic generator — no service prices published.
 */
import { MARRIAGE_SERVICES, MARRIAGE_COUNTRIES } from "./index";
import { liftFlatToFloor } from "../_qa/topup-merge";
import { MARRIAGE_TOPUP_AXIS } from "../_qa/topup-axes-2";

export type MarriageQA = {
  slug: string;
  q: string;
  a: string;
  synonyms: string[];
  serviceSlug?: string;
};

export const MARRIAGE_REVIEWED_TH = "สิงหาคม 2569";
export const MARRIAGE_REVIEWED_ISO = "2026-08-06T00:00:00.000Z";

const ASOF = `ข้อมูลทบทวน ณ ${MARRIAGE_REVIEWED_TH} หลักเกณฑ์ เอกสาร และกำหนดเวลาขึ้นอยู่กับสำนักทะเบียนอำเภอ สถานทูตของคู่สมรส กรมการกงสุล และศาลที่มีเขตอำนาจ ควรตรวจประกาศล่าสุดก่อนดำเนินการ เว็บไซต์ไม่แสดงค่าบริการ กรุณาสอบถามทางโทรศัพท์ LINE หรืออีเมล`;

const CORE: MarriageQA[] = [
  {
    slug: "marriage-registration-order",
    q: "จดทะเบียนสมรสไทย-ต่างชาติต้องทำขั้นตอนใดก่อนหลัง",
    a: `ลำดับที่ใช้กันทั่วไปคือ ฝ่ายต่างชาติขอหนังสือรับรองสถานะการสมรสหรือหนังสือรับรองความเป็นโสดจากสถานทูตหรือหน่วยงานต้นทาง จากนั้นนำไปแปลเป็นภาษาไทยและยื่นรับรองนิติกรณ์ที่กรมการกงสุล แล้วจึงนำเอกสารที่รับรองแล้วไปยื่นจดทะเบียนที่สำนักทะเบียนอำเภอหรือเขต ลำดับที่สำนักทะเบียนแต่ละแห่งกำหนดอาจต่างกันในรายละเอียด ควรสอบถามอำเภอที่จะจดก่อนล่วงหน้า ${ASOF}`,
    synonyms: ["ขั้นตอนจดทะเบียนสมรสต่างชาติ", "marriage registration Thailand steps"],
  },
  {
    slug: "marriage-affirmation-validity",
    q: "หนังสือรับรองสถานะโสดจากสถานทูตมีอายุใช้งานนานเท่าไร",
    a: `ไม่มีอายุมาตรฐานเดียว เพราะสำนักทะเบียนมักกำหนดว่าเอกสารต้องออกใหม่ภายในระยะเวลาไม่นานก่อนวันยื่น และสถานทูตแต่ละแห่งกำหนดรูปแบบเอกสารต่างกัน แนวปฏิบัติที่ปลอดภัยคือวางแผนให้ขอเอกสาร แปล รับรอง และยื่นจดทะเบียนภายในช่วงเวลาต่อเนื่องกัน ไม่ควรขอเอกสารทิ้งไว้ล่วงหน้าหลายเดือน ${ASOF}`,
    synonyms: ["อายุหนังสือรับรองโสด", "affirmation of freedom to marry validity"],
  },
  {
    slug: "marriage-consular-fee",
    q: "ค่าธรรมเนียมรับรองนิติกรณ์เอกสารสมรสที่กรมการกงสุลเท่าไร",
    a: `อัตราที่กรมการกงสุลประกาศคือ 200 บาทต่อตราประทับสำหรับบริการปกติ และ 400 บาทต่อตราประทับสำหรับบริการด่วน จำนวนตราประทับขึ้นอยู่กับจำนวนเอกสารและจำนวนหน้าที่ต้องรับรอง ค่าธรรมเนียมนี้เป็นของหน่วยงานราชการและชำระตรงต่อหน่วยงาน แยกจากค่าบริการแปลหรือค่าดำเนินการซึ่งประเมินเป็นรายเคส ${ASOF}`,
    synonyms: ["ค่าธรรมเนียมนิติกรณ์ 200 400 บาท", "MFA legalisation fee marriage documents"],
  },
  {
    slug: "marriage-name-mismatch",
    q: "ชื่อในเอกสารสะกดไม่ตรงกับหนังสือเดินทางจะจดทะเบียนได้หรือไม่",
    a: `ควรแก้ให้ตรงกันก่อนยื่น เพราะการสะกดที่ต่างกันทำให้สำนักทะเบียนหรือหน่วยงานปลายทางตีกลับ และกระทบต่อการใช้ทะเบียนสมรสในเรื่องวีซ่า ทรัพย์สิน และมรดกภายหลัง วิธีแก้คือขอเอกสารต้นทางที่สะกดตรงกับหนังสือเดินทาง หรือแนบหลักฐานการเปลี่ยนชื่อที่ผ่านการรับรองอย่างถูกต้องพร้อมคำแปล ${ASOF}`,
    synonyms: ["ชื่อไม่ตรงพาสปอร์ตจดทะเบียนสมรส", "name mismatch marriage documents Thailand"],
  },
  {
    slug: "marriage-recognition-abroad",
    q: "ทะเบียนสมรสไทยใช้ในต่างประเทศได้เลยหรือต้องรับรองเพิ่ม",
    a: `ส่วนใหญ่ต้องมีคำแปลและการรับรองก่อนใช้ในต่างประเทศ โดยทั่วไปคือรับรองนิติกรณ์ที่กรมการกงสุล และในบางประเทศต้องรับรองเพิ่มที่สถานทูตของประเทศนั้นในไทย เมื่ออนุสัญญา Apostille มีผลกับประเทศไทยวันที่ 28 กุมภาพันธ์ 2570 เอกสารที่ใช้ระหว่างประเทศภาคีอาจใช้ Apostille แทนการรับรองหลายชั้นได้ แต่ยังต้องเป็นไปตามที่หน่วยงานปลายทางกำหนด ${ASOF}`,
    synonyms: ["ใช้ทะเบียนสมรสไทยต่างประเทศ", "Thai marriage certificate abroad legalisation"],
  },
  {
    slug: "marriage-divorce-jurisdiction",
    q: "คู่สมรสไทย-ต่างชาติจะหย่าที่ไทยหรือต่างประเทศดีกว่า",
    a: `ขึ้นอยู่กับที่จดทะเบียน ที่อยู่ปัจจุบันของคู่สมรส ทรัพย์สินที่ต้องแบ่ง และเรื่องบุตร หากตกลงกันได้และจดทะเบียนที่อำเภอไทย การหย่าโดยความยินยอมที่อำเภอมักเร็วกว่า แต่ถ้ามีข้อพิพาทเรื่องบุตรหรือทรัพย์สิน จะต้องดำเนินคดีที่ศาลที่มีเขตอำนาจ คำพิพากษาจากต่างประเทศอาจต้องผ่านขั้นตอนรับรองหรือฟ้องบังคับในไทยก่อนใช้ผล จึงควรประเมินข้อเท็จจริงกับทนายก่อนเริ่ม ${ASOF}`,
    synonyms: ["หย่าไทยหรือต่างประเทศ", "divorce jurisdiction Thai foreign couple"],
  },
];

function serviceQA(s: (typeof MARRIAGE_SERVICES)[number]): MarriageQA[] {
  const rows: Array<[string, string, string]> = [
    ["what", `${s.nameTh} คืออะไรและเหมาะกับใคร`, `${s.nameTh} (${s.nameEn}) ใช้ในกรณี ${s.intent} หน่วยงานที่เกี่ยวข้องหลักคือ ${s.gov} กรอบเวลาโดยทั่วไปประมาณ ${s.turnaround} ซึ่งขึ้นกับความครบถ้วนของเอกสารและคิวของหน่วยงานหรือศาล ${ASOF}`],
    ["documents", `${s.nameTh} ต้องเตรียมเอกสารอะไร`, `เอกสารตั้งต้นได้แก่ หนังสือเดินทางหรือบัตรประชาชนของทั้งสองฝ่าย ทะเบียนบ้าน เอกสารแสดงสถานะการสมรสหรือการหย่าเดิม และเอกสารต้นทางที่ออกโดยหน่วยงานของคู่สมรสต่างชาติ เอกสารภาษาต่างประเทศต้องมีคำแปลและการรับรองตามที่ ${s.gov} กำหนด ${ASOF}`],
    ["steps", `${s.nameTh} มีขั้นตอนอย่างไร`, `เริ่มจากตรวจสถานะทางกฎหมายของทั้งสองฝ่าย ขอเอกสารต้นทาง แปลและรับรองเอกสาร ยื่นเรื่องต่อ ${s.gov} และเข้าให้ถ้อยคำหรือลงนามตามที่กำหนด สุดท้ายรับเอกสารผลลัพธ์และตรวจความถูกต้องของชื่อ วันที่ และรายละเอียดก่อนนำไปใช้ต่อ ${ASOF}`],
    ["timeline", `${s.nameTh} ใช้เวลานานเท่าไร`, `กรอบเวลาอ้างอิงประมาณ ${s.turnaround} นับจากเอกสารครบ ระยะเวลาจริงขึ้นกับ ${s.gov} การนัดหมาย และกรณีที่มีข้อพิพาทหรือเอกสารต้องขอใหม่จากต่างประเทศ ไม่ควรกำหนดวันสำคัญหรือเดินทางก่อนได้ผลชัดเจน ${ASOF}`],
    ["risks", `${s.nameTh} มีข้อควรระวังอะไร`, `ควรระวังเอกสารที่หมดอายุระหว่างขั้นตอน การแปลที่ไม่ตรงกับชื่อในหนังสือเดินทาง การใช้แบบฟอร์มที่ไม่ตรงกับสำนักทะเบียนหรือศาลนั้น และการทำข้อตกลงเรื่องทรัพย์สินหรือบุตรโดยไม่ตรวจผลทางกฎหมายในทั้งสองประเทศ ${ASOF}`],
    ["quote", `${s.nameTh} คิดค่าบริการอย่างไร`, `ค่าบริการประเมินเป็นรายเคสตามจำนวนเอกสาร ภาษาที่ต้องแปล จำนวนครั้งที่ต้องเข้าพบหน่วยงาน และความซับซ้อนของข้อเท็จจริง เว็บไซต์จึงไม่แสดงราคาเหมารวม ค่าธรรมเนียมราชการและค่าธรรมเนียมศาลเป็นไปตามอัตราที่หน่วยงานประกาศ สอบถามขอบเขตงานได้ทางโทรศัพท์ LINE หรืออีเมล ${ASOF}`],
  ];
  return rows.map(([suffix, q, a]) => ({
    slug: `${s.slug}-${suffix}`,
    q,
    a,
    synonyms: [`${s.nameTh} ${suffix}`, `${s.nameEn.toLowerCase()} ${suffix}`],
    serviceSlug: s.slug,
  }));
}

function matrixQA(): MarriageQA[] {
  const out: MarriageQA[] = [];
  for (const s of MARRIAGE_SERVICES) {
    for (const c of MARRIAGE_COUNTRIES) {
      out.push({
        slug: `${s.slug}-${c.slug}-docs`,
        q: `คู่สมรสสัญชาติ${c.nameTh}ทำ ${s.nameTh} ต้องใช้เอกสารต้นทางอะไร`,
        a: `สำหรับคู่สมรสสัญชาติ${c.nameTh} (${c.nameEn}) ต้องตรวจว่าสถานทูตหรือหน่วยงานของ${c.nameTh}ออกเอกสารรับรองสถานะการสมรสในรูปแบบใด ต้องนัดหมายล่วงหน้าหรือไม่ และเอกสารต้องผ่านการรับรองชั้นใดก่อนใช้ในไทย จากนั้นแปลเป็นภาษาไทยและรับรองตามที่ ${s.gov} กำหนดจึงยื่นเรื่องได้ ${ASOF}`,
        synonyms: [`${s.nameTh} ${c.nameTh}`, `${s.nameEn} ${c.nameEn} documents`],
        serviceSlug: s.slug,
      });
      out.push({
        slug: `${s.slug}-${c.slug}-process`,
        q: `คู่สมรสสัญชาติ${c.nameTh}ควรเริ่ม ${s.nameTh} จากขั้นตอนใด`,
        a: `เริ่มจากติดต่อหน่วยงานของ${c.nameTh}เพื่อขอเอกสารต้นทาง ซึ่งมักเป็นขั้นตอนที่ใช้เวลานานที่สุด จากนั้นแปลและรับรองเอกสาร แล้วยื่นต่อ ${s.gov} โดยกรอบเวลารวมโดยทั่วไปประมาณ ${s.turnaround} ควรเผื่อเวลาสำหรับการนัดหมายและการขอเอกสารเพิ่มเติม ${ASOF}`,
        synonyms: [`${s.nameTh} ขั้นตอน ${c.nameTh}`, `${s.nameEn} process ${c.nameEn}`],
        serviceSlug: s.slug,
      });
      out.push({
        slug: `${s.slug}-${c.slug}-recognition`,
        q: `ผลของ ${s.nameTh} จะใช้ใน${c.nameTh}ได้หรือไม่`,
        a: `การรับรองผลใน${c.nameTh}ขึ้นอยู่กับกฎหมายและแนวปฏิบัติของ${c.nameTh}เอง โดยทั่วไปต้องนำเอกสารผลลัพธ์จากไทยไปแปลและรับรองตามเส้นทางที่หน่วยงานของ${c.nameTh}กำหนด บางกรณีต้องแจ้งจดหรือบันทึกเพิ่มที่หน่วยงานทะเบียนของ${c.nameTh} ควรตรวจข้อกำหนดกับหน่วยงานนั้นก่อนดำเนินการเพื่อไม่ต้องรับรองซ้ำ ${ASOF}`,
        synonyms: [`${s.nameTh} รับรองใน ${c.nameTh}`, `${s.nameEn} recognition ${c.nameEn}`],
        serviceSlug: s.slug,
      });
    }
  }
  return out;
}

export const MARRIAGE_QA_ALL: MarriageQA[] = (() => {
  const all = [...CORE, ...MARRIAGE_SERVICES.flatMap(serviceQA), ...matrixQA()];
  const seen = new Set<string>();
  return all.filter((x) => (seen.has(x.slug) ? false : (seen.add(x.slug), true)));
})();

liftFlatToFloor(MARRIAGE_QA_ALL, {
  prefix: "marriage-t7",
  asOf: `ข้อมูลทบทวน ณ ${MARRIAGE_REVIEWED_TH} แนวปฏิบัติของแต่ละสำนักทะเบียนและสถานทูตต่างกันได้ ควรยืนยันก่อนดำเนินการ เว็บไซต์ไม่แสดงค่าบริการ กรุณาสอบถามทางโทรศัพท์ LINE หรืออีเมล`,
  authorityTh: "สำนักทะเบียนอำเภอหรือเขต และสถานทูตที่มีเขตอำนาจ",
  clusterTh: "งานจดทะเบียนสมรสและงานทะเบียนครอบครัว",
  primary: MARRIAGE_TOPUP_AXIS,
});

export const MARRIAGE_QA_BY_SLUG: Record<string, MarriageQA> = Object.fromEntries(
  MARRIAGE_QA_ALL.map((x) => [x.slug, x]),
);

export const MARRIAGE_QA_PHRASING_COUNT = MARRIAGE_QA_ALL.reduce(
  (n, qa) => n + 1 + qa.synonyms.length,
  0,
);
